import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import { useInstallApp, type ManualStepsKind } from './useInstallApp';

const DISMISSED_KEY = 'waiter:install-dismissed';

/** Les etapes montrees pour chaque famille de navigateur (cf. useInstallApp). */
const MANUAL_STEPS: Record<ManualStepsKind, readonly string[]> = {
  ios: ['ios.step1', 'ios.step2', 'ios.step3'],
  browserMenu: ['browserMenu.step1', 'browserMenu.step2', 'browserMenu.step3'],
  inAppBrowser: ['inAppBrowser.step1', 'inAppBrowser.step2'],
};

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    // localStorage indisponible (navigation privee) : on propose, c'est tout.
    return false;
  }
}

function writeDismissed(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // Sans stockage, le bandeau revient au prochain chargement. Acceptable.
  }
}

interface InstallAppPromptProps {
  /**
   * `banner` : la bande en haut de la liste des tables, refermable une fois
   * pour toutes, et qui ne s'affiche que si le navigateur nous laisse tenir
   * parole. `row` : la ligne de la feuille d'identite, TOUJOURS rendue — c'est
   * le seul endroit ou l'installation se retrouve, et un endroit qui n'est vide
   * qu'une fois sur deux ne se cherche plus.
   */
  variant: 'banner' | 'row';
}

/**
 * SC-906 — « installer le carnet sur l'ecran d'accueil ».
 *
 * L'issue depend du navigateur, et c'est lui qui tranche, pas nous :
 *  - Chrome / Edge / Samsung Internet : ils pretent leur boite d'installation,
 *    un seul appui suffit ;
 *  - iOS (Safari comme les autres, tous sur WebKit) : aucun site ne peut poser
 *    une icone, la fiche montre les deux gestes du menu « Partager » ;
 *  - Firefox Android et consorts : meme fiche, avec le geste de LEUR menu ;
 *  - navigateur encapsule (WeChat, Messenger…) : rien n'est installable la, la
 *    fiche dit d'ouvrir la page dans un vrai navigateur ;
 *  - Chrome qui ne dit rien (carnet deja installe, ou criteres pas remplis) :
 *    la fiche du menu, comme Firefox — c'est bien son menu qui reste l'issue.
 * Seule la bande du haut se tait quand il n'y a rien a promettre ; la ligne de
 * la feuille d'identite repond dans tous les cas.
 */
export default function InstallAppPrompt({ variant }: InstallAppPromptProps) {
  const { t } = useTranslation();
  const { canPrompt, manualSteps, stepsKind, promptInstall } = useInstallApp();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [stepsOpen, setStepsOpen] = useState(false);

  // La bande du haut ne s'invite que quand on a de quoi tenir parole. La ligne
  // de la feuille d'identite, elle, est offerte EN PERMANENCE : elle manquait
  // exactement quand on la cherche, parce que Chrome cesse d'emettre
  // `beforeinstallprompt` des que le carnet est installe (et ne l'emet pas
  // encore tant que les criteres ne sont pas remplis), tandis que `manualSteps`
  // se tait sur tout navigateur du genre. Le serveur ouvrait la feuille et n'y
  // trouvait rien, sans jamais savoir pourquoi.
  if (variant === 'banner' && (dismissed || (!canPrompt && !manualSteps))) return null;

  const handleInstall = () => {
    if (canPrompt) {
      void promptInstall();
      return;
    }
    setStepsOpen(true);
  };

  const steps = (
    <Modal
      open={stepsOpen}
      onClose={() => setStepsOpen(false)}
      ariaLabel={t('waiter.installApp.stepsTitle')}
      instant
    >
      <div
        data-testid="install-app-steps"
        data-kind={stepsKind}
        className="p-4 flex flex-col gap-3"
      >
        <h2 className="font-sans text-[18px] font-bold">{t('waiter.installApp.stepsTitle')}</h2>
        <ol className="flex flex-col gap-2 ps-5 list-decimal font-sans text-base text-ink">
          {MANUAL_STEPS[stepsKind].map((key) => (
            <li key={key}>{t(`waiter.installApp.${key}`)}</li>
          ))}
        </ol>
        <p className="font-sans text-sm text-ink-mute">
          {t(`waiter.installApp.${stepsKind}.note`)}
        </p>
        <button
          type="button"
          data-testid="install-app-steps-close"
          onClick={() => setStepsOpen(false)}
          className="shrink-0 h-12 rounded-btn border-2 border-ink bg-paper font-sans text-[18px] font-bold text-ink"
        >
          {t('common.close')}
        </button>
      </div>
    </Modal>
  );

  if (variant === 'row') {
    return (
      <>
        <button
          type="button"
          data-testid="install-app-row"
          onClick={handleInstall}
          className="shrink-0 h-12 rounded-btn border-2 border-ink bg-paper font-sans text-[18px] font-bold text-ink"
        >
          {t('waiter.installApp.action')}
        </button>
        {steps}
      </>
    );
  }

  return (
    <>
      <div className="flex items-stretch bg-ink text-paper">
        <button
          type="button"
          data-testid="install-app-banner"
          onClick={handleInstall}
          className="flex-1 min-h-[40px] px-3 py-1.5 text-start font-sans text-sm font-bold"
        >
          <span aria-hidden="true" className="pe-1.5">
            ⤓
          </span>
          {t('waiter.installApp.banner')}
        </button>
        <button
          type="button"
          data-testid="install-app-dismiss"
          aria-label={t('waiter.installApp.dismiss')}
          onClick={() => {
            writeDismissed();
            setDismissed(true);
          }}
          className="w-11 min-h-[40px] font-sans text-base"
        >
          ✕
        </button>
      </div>
      {steps}
    </>
  );
}
